import { useEffect, useRef } from 'react'

/**
 * Captures a hardware barcode/QR reader working in keyboard-wedge mode: it types the whole payload
 * very fast and closes with a terminator key (Enter by default).
 *
 * Deliberately ignores keystrokes aimed at a text field — that case is handled by the field itself
 * (which also serves manual typing). So this hook covers "scan with nothing focused", which is what
 * a MOS widget needs to react to a scan without opening a dialog first.
 *
 * Two things keep a burst from reaching anything but its reader. Several listeners can be mounted at
 * once (two lookup widgets, a lookup next to a QR Reader): the first one to see the terminator takes the
 * burst and marks the key as handled, so the others skip it. And a burst typed while a button has the
 * focus would click it — a space or Enter in the payload — so, once a burst is under way, its keys are
 * swallowed. The listener is in the capture phase so it runs before the focused element's own handler,
 * which can then tell by `defaultPrevented` that the key was not meant for it.
 */

const isTypingTarget = (target) => {
  if (!target) return false
  const tag = String(target.tagName || '').toLowerCase()
  return tag === 'input' || tag === 'textarea' || target.isContentEditable === true
}

/**
 * @param {object} params
 * @param {boolean} params.enabled listen only while true
 * @param {(raw: string) => void} params.onScan called with the buffered payload on the terminator key
 * @param {number} params.minLength shorter bursts are discarded (stray keystrokes)
 * @param {string} params.terminator key that ends a scan
 * @param {number} params.idleMs drop the buffer after this long without keystrokes. Generous on purpose:
 *   too short would cut a slow scanner mid-burst (feature broken), while too long only lets stray keys
 *   accumulate — harmless, since minLength and the payload parsing reject them.
 */
const useQrInput = ({ enabled = true, onScan, minLength = 4, terminator = 'Enter', idleMs = 200 }) => {
  const buffer = useRef('')
  const idleTimer = useRef(0)
  // A button clicks on the release of Space, so the release of a swallowed Space is swallowed too.
  const swallowedSpace = useRef(false)
  // Keep the latest callback without re-subscribing on every parent render.
  const onScanRef = useRef(onScan)
  onScanRef.current = onScan

  useEffect(() => {
    buffer.current = ''
    if (!enabled) return

    const clearIdle = () => {
      if (idleTimer.current) {
        clearTimeout(idleTimer.current)
        idleTimer.current = 0
      }
    }

    const handleKeyDown = (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return
      if (isTypingTarget(event.target)) return

      if (event.key === terminator) {
        const scanned = buffer.current
        buffer.current = ''
        clearIdle()
        // Another listener took this burst already.
        if (event.defaultPrevented) return
        if (scanned.length >= minLength) {
          event.preventDefault()
          onScanRef.current(scanned)
        }
        return
      }

      if (event.key.length !== 1) return // ignore Shift, Tab, F5…
      // From the second key on it is a burst, not somebody pressing a key: a lone Space or Enter keeps
      // activating the focused button.
      if (buffer.current !== '') {
        event.preventDefault()
        if (event.key === ' ') swallowedSpace.current = true
      }
      buffer.current += event.key
      clearIdle()
      idleTimer.current = setTimeout(() => { buffer.current = '' }, idleMs)
    }

    const handleKeyUp = (event) => {
      if (event.key !== ' ' || !swallowedSpace.current) return
      swallowedSpace.current = false
      event.preventDefault()
    }

    window.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('keyup', handleKeyUp, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('keyup', handleKeyUp, true)
      swallowedSpace.current = false
      clearIdle()
      buffer.current = ''
    }
  }, [enabled, minLength, terminator, idleMs])
}

export default useQrInput
